<template>
	<EChart v-if="chartReady" :option="chartOption"
		@chart-mouseover="onSeriesHover" @chart-mouseout="onSeriesOut" />
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import { widenedSeriesPalette } from "@/utils/colorPalette";
import tooltipFocusMixin from "./tooltipFocusMixin";

// Chart genérico de los cards del tab KPIs: combina barras (arriba/abajo,
// apilables) y líneas sobre un eje X mensual compartido, con hasta DOS ejes Y
// derivados de las unidades de las series (money / count / percent). Cada
// card del tab declara sus series con `{ name, type, unit, data, down?,
// stack? }` y este componente resuelve ejes, colores, leyenda y tooltip —
// mismas convenciones visuales que SalesClientsActivityChart.
//
// Color por ROL: las BARRAS toman la gama del tema (accent = principal,
// segment-3 la clara); las LÍNEAS de charts mixtos sus tokens tinta
// (line-1..5, matices contrastantes SIEMPRE más oscuros que las barras —
// misma familia para ambos las fundía a la vista); los charts SOLO-líneas
// degradan la familia del tema (line-1 → segment-3, rango ensanchado); los
// STACKS degradan entre segment-1 y segment-3. Temas sin tokens de línea
// caen al extremo oscuro del degradé. Una serie puede fijar su color con
// `colorToken` (un token del tema) y queda FUERA del reparto por índice:
// lo usa Rentabilidad para que cada margen conserve su identidad aunque
// falten escalones (Servicios).
const BAR_COLOR_ANCHORS = [
	"--report-chart-accent",
	"--report-chart-segment-3",
	"--report-chart-segment-2",
	"--report-chart-other",
];
const LINE_COLOR_ANCHORS = [
	"--report-chart-line-1",
	"--report-chart-line-2",
	"--report-chart-line-3",
	"--report-chart-line-4",
	"--report-chart-line-5",
];
const STACK_DARK_ANCHOR = "--report-chart-segment-1";
const STACK_LIGHT_ANCHOR = "--report-chart-segment-3";
const TEXT_MUTED_ANCHOR = "--report-text-muted";
const TEXT_ANCHOR = "--report-text";
const BORDER_ANCHOR = "--report-border";
const TOOLTIP_BG_ANCHOR = "--report-tooltip-bg";
const TOOLTIP_TEXT_ANCHOR = "--report-tooltip-text";

// Formatters por unidad. Percent en enteros (regla del repo: los % de los
// reportes se muestran sin decimales). `ratio` son veces (x) — ahí el
// decimal ES la información (0.4x vs 1.2x), no aplica la regla de enteros.
const AXIS_FORMATTERS = {
	money: (val) => parseToCompactMoney(val),
	count: (val) => Math.round(val),
	percent: (val) => `${Math.round(val)}%`,
	ratio: (val) => `${Math.round(val * 10) / 10}`,
};
const TOOLTIP_FORMATTERS = {
	money: (val) => parseToMoney(val),
	// Counts enteros se muestran enteros; los FRACCIONALES (headcount por
	// equipo de KPIs Personas: una persona repartida entre sus equipos)
	// conservan hasta 2 decimales — redondearlos a entero mostraría "0"
	// para una participación de 0.25.
	count: (val) => {
		const rounded = Math.round(val);
		return Math.abs(val - rounded) < 1e-9
			? `${rounded}`
			: `${Math.round(val * 100) / 100}`;
	},
	percent: (val) => `${Math.round(val)}%`,
	ratio: (val) => `${(Math.round(val * 100) / 100)}x`,
};

export default {
	name: "KpiComposedChart",

	components: { EChart },

	mixins: [tooltipFocusMixin],

	props: {
		monthHeaders: { type: Array, required: true },
		// [{ name, type: 'bar'|'line', unit: 'money'|'count'|'percent'|'ratio',
		//    data: [Number|null], down?: Boolean, stack?: String,
		//    colorToken?: String }]
		// `down` invierte la serie (barra hacia abajo: salientes/pérdidas).
		// `colorToken` ('--report-chart-line-4') fija el color de esa serie
		// leyéndolo del tema del host, en vez del reparto por índice.
		series: { type: Array, required: true },
		// Puntos sobre las líneas (círculo relleno del color de la serie).
		// Default apagado: los charts de KPIs siguen como hasta ahora.
		showSymbols: { type: Boolean, default: false },
	},

	data() {
		return {
			chartReady: false,
			barPalette: [],
			linePalette: [],
			stackDark: "",
			stackLight: "",
			textColor: "",
			textMutedColor: "",
			borderColor: "",
			chartBg: "",
			tooltipBg: "",
			tooltipText: "",
			// token → color resuelto, para las series con `colorToken`.
			tokenColors: {},
		};
	},

	computed: {
		// Unidades en orden de aparición → índice de eje Y (máx 2; una tercera
		// unidad comparte el eje derecho antes que agregar un tercer eje ilegible).
		unitAxisIndex() {
			const units = [];
			this.series.forEach((s) => {
				if (!units.includes(s.unit)) units.push(s.unit);
			});
			const map = {};
			units.forEach((unit, i) => { map[unit] = Math.min(i, 1); });
			return map;
		},

		axisUnits() {
			const units = [];
			Object.entries(this.unitAxisIndex).forEach(([unit, idx]) => {
				if (!units[idx]) units[idx] = unit;
			});
			return units;
		},

		hasDownSeries() {
			return this.series.some((s) => s.down);
		},

		// Color por serie según su ROL, no su índice:
		// - Stacks: degradé oscuro→claro entre segment-1 y segment-3
		//   (widenedSeriesPalette, primera serie = más oscura — misma
		//   convención que SalesClientsStackedChart).
		// - Líneas de charts MIXTOS (sobre barras): los tokens tinta
		//   (line-1..5, matices contrastantes) — la línea siempre es más
		//   oscura que la barra Y de otro matiz, así se distinguen de un
		//   vistazo.
		// - Charts SOLO-líneas con 2+ series (Márgenes, equipos multi-línea):
		//   rampa de la MISMA familia del tema — del tinta (line-1) al claro
		//   (segment-3), ensanchada por widenedSeriesPalette para que los
		//   pasos queden bien separados y cada línea sea identificable. Acá
		//   no hay barras de las que separarse por matiz, y la gama única
		//   mantiene la identidad del tab.
		// - Barras sueltas: la gama del tema (principal → clara → media).
		seriesColors() {
			const colors = new Array(this.series.length).fill(null);

			// Series con color fijado por token: se resuelven primero y no
			// participan del reparto por índice de las demás.
			const tokenColors = this.tokenColors || {};
			this.series.forEach((spec, i) => {
				if (spec.colorToken && tokenColors[spec.colorToken]) {
					colors[i] = tokenColors[spec.colorToken];
				}
			});

			const stacks = new Map();
			this.series.forEach((spec, i) => {
				if (colors[i] != null) return;
				if (spec.type !== "bar" || !spec.stack) return;
				if (!stacks.has(spec.stack)) stacks.set(spec.stack, []);
				stacks.get(spec.stack).push(i);
			});
			stacks.forEach((seriesIdxs) => {
				const steps = widenedSeriesPalette(
					this.stackDark, this.stackLight, seriesIdxs.length,
				);
				seriesIdxs.forEach((seriesIdx, k) => { colors[seriesIdx] = steps[k]; });
			});

			const lineIdxs = [];
			const barIdxs = [];
			this.series.forEach((spec, i) => {
				if (colors[i] != null) return;
				(spec.type === "line" ? lineIdxs : barIdxs).push(i);
			});
			const lineOnly = barIdxs.length === 0 && stacks.size === 0;
			if (lineOnly && lineIdxs.length > 1) {
				const lineDark = this.linePalette[0] || this.stackDark;
				const steps = widenedSeriesPalette(
					lineDark, this.stackLight, lineIdxs.length,
				);
				lineIdxs.forEach((seriesIdx, k) => { colors[seriesIdx] = steps[k]; });
			} else {
				lineIdxs.forEach((seriesIdx, k) => {
					colors[seriesIdx] = this.linePalette[
						Math.min(k, this.linePalette.length - 1)
					];
				});
			}
			barIdxs.forEach((seriesIdx, k) => {
				colors[seriesIdx] = this.barPalette[
					Math.min(k, this.barPalette.length - 1)
				];
			});
			return colors;
		},

		chartOption() {
			const yAxis = this.axisUnits.map((unit, idx) => ({
				type: "value",
				position: idx === 0 ? "left" : "right",
				...(unit === "count" ? { minInterval: 1 } : {}),
				axisLine: { show: false },
				axisTick: { show: false },
				// Sólo el eje izquierdo pinta la grid, como en el resto de los
				// charts del reporte.
				splitLine: idx === 0
					? { lineStyle: { color: this.borderColor, type: "dashed" } }
					: { show: false },
				axisLabel: {
					color: this.textMutedColor,
					fontSize: 11,
					formatter: AXIS_FORMATTERS[unit] || ((v) => v),
				},
			}));

			return {
				grid: { top: 44, right: 48, bottom: 28, left: 48, containLabel: false },
				legend: {
					show: true,
					left: 0,
					top: 0,
					itemGap: 14,
					itemWidth: 14,
					itemHeight: 8,
					textStyle: { color: this.textColor, fontSize: 11, fontWeight: 500 },
				},
				tooltip: {
					trigger: "axis",
					confine: true,
					extraCssText: "max-width:280px; white-space:normal; word-break:break-word;",
					backgroundColor: this.tooltipBg,
					borderColor: this.tooltipBg,
					textStyle: { color: this.tooltipText, fontSize: 12 },
					formatter: (params) => {
						if (!Array.isArray(params) || params.length === 0) return "";
						const rows = params.filter((p) => p.value != null).map((p) => {
							const spec = this.series[p.seriesIndex] || {};
							const format = TOOLTIP_FORMATTERS[spec.unit] || ((v) => v);
							// Las series `down` viajan negadas para el render;
							// el tooltip muestra la magnitud real con su signo.
							const value = format(p.value);
							return `
								<div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;${this.focusRowStyle(p.seriesName)}">
									<span style="min-width:0;">${p.marker}${p.seriesName}</span>
									<span style="flex-shrink:0;">${value}</span>
								</div>
							`;
						}).join("");
						return `
							<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValueLabel}</div>
							${rows}
						`;
					},
				},
				xAxis: {
					type: "category",
					data: this.monthHeaders,
					axisLine: { lineStyle: { color: this.borderColor } },
					axisTick: { show: false },
					axisLabel: { color: this.textMutedColor, fontSize: 11, margin: 10 },
				},
				yAxis,
				series: this.series.map((spec, idx) => this.buildSeries(spec, idx)),
			};
		},
	},

	watch: {
		// Series nuevas pueden traer tokens que aún no se resolvieron.
		series() {
			this.resolveTokenColors();
		},
	},

	mounted() {
		this.resolveCssColors();
		this.chartReady = this.barPalette.length > 0;
	},

	methods: {
		buildSeries(spec, idx) {
			const color = this.seriesColors[idx];
			const data = (spec.data || []).map(
				(value) => (value == null ? null : (spec.down ? -value : value)),
			);
			const base = {
				name: spec.name,
				yAxisIndex: this.unitAxisIndex[spec.unit] || 0,
				data,
				emphasis: { focus: "series" },
			};
			if (spec.type === "line") {
				return {
					...base,
					type: "line",
					smooth: true,
					showSymbol: this.showSymbols,
					symbol: "circle",
					symbolSize: 6,
					// Un null es "sin dato" (ej. sin mes previo para el MoM):
					// la línea se corta, no se interpola.
					connectNulls: false,
					// 2.5px: sobre barras la línea necesita más peso que los
					// 2px de los charts solo-línea para no perderse.
					lineStyle: { width: 2.5, color },
					itemStyle: { color },
				};
			}
			return {
				...base,
				type: "bar",
				stack: spec.stack,
				barMaxWidth: 28,
				itemStyle: {
					color,
					borderRadius: 0,
					// Hairline entre segmentos apilados: sin él los pasos del
					// degradé se funden en una sola barra.
					...(spec.stack
						? { borderColor: this.chartBg, borderWidth: 1 }
						: {}),
				},
			};
		},

		themeStyle() {
			// Tokens leídos del host dentro del [data-theme] del tab — mismo
			// criterio que los demás charts (documentElement ignoraría el tema).
			const host = this.$el && this.$el.nodeType === 1
				? this.$el
				: (this.$el && this.$el.parentElement) || document.documentElement;
			return getComputedStyle(host);
		},

		resolveTokenColors(root = null) {
			if (typeof window === "undefined") return;
			const tokens = this.series
				.map((spec) => spec.colorToken)
				.filter((token) => token && !this.tokenColors[token]);
			if (!tokens.length) return;
			const style = root || this.themeStyle();
			const resolved = { ...this.tokenColors };
			tokens.forEach((token) => {
				const color = style.getPropertyValue(token).trim();
				if (color) resolved[token] = color;
			});
			this.tokenColors = resolved;
		},

		resolveCssColors() {
			if (typeof window === "undefined") return;
			const root = this.themeStyle();
			this.resolveTokenColors(root);
			this.barPalette = BAR_COLOR_ANCHORS
				.map((anchor) => root.getPropertyValue(anchor).trim())
				.filter(Boolean);
			this.linePalette = LINE_COLOR_ANCHORS
				.map((anchor) => root.getPropertyValue(anchor).trim())
				.filter(Boolean);
			this.stackDark = root.getPropertyValue(STACK_DARK_ANCHOR).trim();
			this.stackLight = root.getPropertyValue(STACK_LIGHT_ANCHOR).trim();
			// Temas sin tokens de línea: el extremo oscuro del degradé, que
			// garantiza "línea más oscura que barra" igual que antes.
			if (!this.linePalette.length && this.stackDark) {
				this.linePalette = [this.stackDark];
			}
			this.textColor = root.getPropertyValue(TEXT_ANCHOR).trim();
			this.textMutedColor = root.getPropertyValue(TEXT_MUTED_ANCHOR).trim();
			this.borderColor = root.getPropertyValue(BORDER_ANCHOR).trim();
			this.chartBg = root.getPropertyValue("--report-bg").trim() || "#ffffff";
			this.tooltipBg = root.getPropertyValue(TOOLTIP_BG_ANCHOR).trim();
			this.tooltipText = root.getPropertyValue(TOOLTIP_TEXT_ANCHOR).trim();
		},
	},
};
</script>

<style scoped lang="scss">
:deep(.echart) {
	width: 100%;
	height: 100%;
}
</style>
